/*
Usage: 
  <skio-cart-upsell item='{{ item | json | escape }}' line='{{ forloop.index }}'></skio-cart-upsell>
  <script src="{{ 'skio-cart-upsell.js' | asset_url }}" type="module"></script>
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js'

// EG normally removes gifts when their trigger disappears. A checkout round trip
// followed by rapid removals can leave an undiscounted gift behind. Reconcile only
// gifts owned by this offer, by cart line key, after cart rendering settles.
function bundleGiftRemovals(cart) {
  const rules = [
    {name: 'Kaizen Bundle Ladder - Bottle - First order', variant: 48877132382503, minimum: 2, plans: [693615558951, 693615591719, 693615624487]},
    {name: 'Kaizen Bundle Ladder - Frother - First order', variant: 50121915957543, minimum: 3, plans: [693615591719, 693615624487]}
  ];
  const updates = {};
  for (const rule of rules) {
    const quantity = cart.items.reduce((sum, item) => sum + (rule.plans.includes(Number(item.selling_plan_allocation?.selling_plan?.id)) ? item.quantity : 0), 0);
    if (quantity >= rule.minimum) continue;
    for (const item of cart.items) {
      if (item.variant_id === rule.variant && item.properties?._Gifted === rule.name) updates[item.key] = 0;
    }
  }
  return updates;
}

let bundleGiftCleanupTimer;
let bundleGiftCleanupRunning = false;
function scheduleBundleGiftCleanup() {
  if (!window.location.pathname.replace(/\/$/, '').endsWith('/cart')) return;
  clearTimeout(bundleGiftCleanupTimer);
  bundleGiftCleanupTimer = setTimeout(async () => {
    if (bundleGiftCleanupRunning) return;
    bundleGiftCleanupRunning = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {headers: {Accept: 'application/json'}});
      if (!response.ok) return;
      const updates = bundleGiftRemovals(await response.json());
      if (!Object.keys(updates).length) return;
      const removed = await fetch(`${root}cart/update.js`, {
        method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({updates})
      });
      if (removed.ok) window.location.reload();
    } catch (error) {
      console.warn('Unable to refresh bundle gifts', error);
    } finally {
      bundleGiftCleanupRunning = false;
    }
  }, 500);
}

const skioCartStyles = css`
  .skio-cart-upsell {
    margin-top: 10px;
    width: 100%;
    padding: 7px 5px;
    background: transparent;
    border: 1px solid #5c432e;
    border-radius: 5px;
    cursor: pointer;
    max-width: 300px;
    color: #5c432e;
  }

  .skio-cart-upgrade {
    margin-top: 10px;
    background: #5c432e; 
    color: #ffffff;
    border: 1px solid #5c432e;
    outline: none;
    cursor: pointer;
    padding: 7px 5px;
    width: 100%;
    border-radius: 5px;
    transition: color 0.25s, background-color 0.25s, border-color 0.25s;
  }

  .skio-cart-upgrade:hover {
    color: #000;
    border-color: #5c432e;  
    background: transparent;
  }
`;

export class SkioCartUpsell extends LitElement {
  static styles = skioCartStyles;

  static properties = {
    item: { type: Object },
    line: { type: Number },

    product: { type: Object },
    selectedVariant: { type: Object },

    skioSellingPlanGroups: {},
    availableSellingPlanGroups: {},

    selectedSellingPlanGroup: {},
    selectedSellingPlan: {},

    moneyFormatter: {},
    currency: { type: String }
  };

  constructor() {
    super();

    this.item = null;
    this.line = null;

    this.product = null;
    this.selectedVariant = null;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.currency = 'USD';

    this.moneyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
    });
  }

  render() {
    // Preserve the chosen bundle plan; do not offer individual frequency changes.
    if (this.item?.properties?._kaizen_offer === 'bundle-ladder') {
      return html`<span class="skio-cart-upsell">${this.item.selling_plan_allocation?.selling_plan?.name || 'Monthly subscription'}</span>`;
    }
    if (!this.item || !this.product || !this.selectedVariant) return;
    return this.selectedSellingPlan
      ? this.changeFrequency()
      : this.upgradeButton();
  }

  upgradeButton = () => {
    if (!this.availableSellingPlanGroups.length) return html``;

    return html`
      <button
        class="skio-cart-upgrade"
        type="button"
        @click=${() =>
          this.selectSellingPlan(
            this.availableSellingPlanGroups[0].selling_plans[0].id
          )
        }>
        Upgrade to subscription
      </button>
    `;
  };

  changeFrequency = () => {
    return html`
      <select
        class="skio-cart-upsell"
        @change=${(e) => this.selectSellingPlan(e.target.value)}>

        ${!this.product.requires_selling_plan ? html`
          <optgroup label="One Time Purchase">
            <option value="">One-time</option>
          </optgroup>
        ` : ''}

        ${this.availableSellingPlanGroups.map(group => html`
          <optgroup label="${group.name} Purchase">
            ${group.selling_plans.map(plan => html`
              <option
                value="${plan.id}"
                .selected=${plan.id == this.selectedSellingPlan?.id}>
                ${plan.name}
              </option>
            `)}
          </optgroup>
        `)}
      </select>
    `;
  };

  updated(changed) {

    if (changed.has('item') && (this.item?.properties?._kaizen_offer === 'bundle-ladder' || this.item?.properties?._Gifted?.startsWith('Kaizen Bundle Ladder - '))) {
      scheduleBundleGiftCleanup();
    }

    // Bundle plans are intentionally absent from the ordinary upsell picker.
    // Never let its initial selection reset a bundle line to a one-time purchase.
    if (this.item?.properties?._kaizen_offer === 'bundle-ladder') return;

    if (changed.has('item') && this.item) {
      this.fetchProduct(this.item.handle);
    }

    if (changed.has('product') && this.product) {

      this.skioSellingPlanGroups =
        (this.product.selling_plan_groups || []).map(group => ({
          ...group,
          selling_plans: group.selling_plans.filter(plan =>
            ![693615526183, 693615558951, 693615591719, 693615624487].includes(Number(plan.id))
          )
        })).filter(group => group.app_id === 'SKIO' && group.selling_plans.length > 0);

      this.selectedVariant =
        (this.product.variants || []).find(
          variant => variant.id == this.item.variant_id
        );

      if (this.item.selling_plan_allocation) {
        const id = this.item.selling_plan_allocation.selling_plan.id;

        this.selectedSellingPlanGroup =
          this.skioSellingPlanGroups.find(group =>
            group.selling_plans.some(plan => plan.id == id)
          );

        this.selectedSellingPlan =
          this.selectedSellingPlanGroup?.selling_plans.find(
            plan => plan.id == id
          );
      }
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {

      const allocations =
        this.selectedVariant.selling_plan_allocations || [];

      // this.log('Variant Allocations:', allocations);

      this.availableSellingPlanGroups =
        this.skioSellingPlanGroups
          .map(group => {

            const mappedPlans =
              group.selling_plans.filter(plan =>
                allocations.some(a =>
                  Number(a.selling_plan_id) === Number(plan.id)
                )
              );

            if (!mappedPlans.length) return null;

            return {
              ...group,
              selling_plans: mappedPlans,
              selected_selling_plan: mappedPlans[0]
            };
          })
          .filter(Boolean);

      // this.log('Filtered Groups:', this.availableSellingPlanGroups);
    }

    if (changed.has('selectedSellingPlan') && this.product) {
      this.updateLineItem();
    }
  }

  async updateLineItem() {
    if (this.item?.properties?._kaizen_offer === 'bundle-ladder') return;
    if (!this.line || !this.item) return
    if (this.item.selling_plan_allocation?.selling_plan?.id == this.selectedSellingPlan?.id) return

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: this.line,
          quantity: this.item.quantity,
          selling_plan: this.selectedSellingPlan?.id ?? null,
          sections: ['cart-drawer'],
        }),
      })

      const cart = await response.json()

      if (Shopify.theme.jsCart !== 'undefined' && window.location.href.includes('/cart')) {
        Shopify.theme.jsCart.updateView(cart, this.line)
      }

      if (typeof Shopify.theme.jsAjaxCart !== 'undefined') {
        Shopify.theme.jsAjaxCart.updateView()
      }

      // Re-render the theme cart drawer via eventBus
      if (window.eventBus) {
        if (cart.sections?.['cart-drawer']) {
          window.eventBus.emit('update:cart:drawer', { sections: cart.sections })
        } else {
          window.eventBus.emit('render:cart:drawer')
        }
      }
    } catch (err) {
      this.error('updateLineItem failed:', err)
    }
  }

  selectSellingPlan(id) {

    if (!id) {
      this.selectedSellingPlanGroup = null;
      this.selectedSellingPlan = null;
      return;
    }

    const group =
      this.availableSellingPlanGroups.find(group =>
        group.selling_plans.some(plan => plan.id == id)
      );

    const selling_plan =
      group?.selling_plans.find(plan => plan.id == id);

    if (selling_plan) {
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    }
    else {
      this.error('Could not find selling plan:', id);
    }
  }

  money(price) {
    return this.moneyFormatter.format(price / 100.0);
  }

  getSectionsToRender() {
    return [
      { section: 'cart-drawer' },
      { section: 'cart-icon-bubble' },
      { section: 'cart-live-region-text' }
    ];
  }

  fetchProduct = async(handle) => {
    try {
      let productCache =
        window.sessionStorage.skioCartProductCache
          ? JSON.parse(window.sessionStorage.skioCartProductCache)
          : [];

      let cachedProduct =
        productCache.find(product => product.handle == handle);

      if (cachedProduct) {
        this.product = cachedProduct;
        return;
      }

      await fetch(`/products/${handle}.js`)
        .then(res => res.json())
        .then(response => {

          this.product = response;

          productCache.push(response);

          window.sessionStorage.skioCartProductCache =
            JSON.stringify(productCache);

          this.requestUpdate();
        });
    } catch (err) {
      this.error('fetchProduct failed:', err)
    }
  };

  log(...args) {
    args.unshift('%c[skio cart upsell]', 'color: #8770f2;');
    console.log(...args);
  }

  error(...args) {
    args.unshift('%c[skio cart upsell]', 'color: red;');
    console.error(...args);
  }
}

customElements.define('skio-cart-upsell', SkioCartUpsell);
     
